// src/pages/AppliedJobs.js — polished & fixed 💅
import React, { useEffect, useState } from "react";
import axios from "axios";
import socket from "../socket";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageSquare, RefreshCw, AlertCircle } from "lucide-react";

const API_WRK =
  process.env.REACT_APP_API_WRK || "http://localhost:5000/api/worker";

const HIDE_STATUSES = new Set([
  "completed",
  "customer_confirmed",
  "auto_confirmed",
  "cancelled",
  "refunded",
  "partial_refund",
]);

const AppliedJobs = () => {
  const [bids, setBids] = useState([]);
  const [selectedTab, setSelectedTab] = useState("submitted");
  const [activeBid, setActiveBid] = useState(null);
  const [newBidAmount, setNewBidAmount] = useState("");
  const [newBidMessage, setNewBidMessage] = useState("");
  const workerId = localStorage.getItem("workerId");
  const navigate = useNavigate();

  // ------- Fetch bids
  const fetchBids = async () => {
    if (!workerId) return;
    try {
      const res = await axios.get(`${API_WRK}/my-bids/${workerId}`);
      setBids(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Error fetching applied jobs:", err);
    }
  };

  // ------- Socket setup
  useEffect(() => {
    if (!workerId) {
      navigate("/worker-login");
      return;
    }
    fetchBids();

    const safeFetch = () => {
      clearTimeout(window._fetchTimer);
      window._fetchTimer = setTimeout(fetchBids, 350);
    };

    try {
      socket.emit("registerWorker", { workerId });

      socket.on("job:update", (update) => {
        if (update.assignedTo === workerId && update.status === "assigned") {
          toast.success("🎉 Your bid was accepted!");
        } else if (["cancelled", "reopened"].includes(update.status)) {
          toast("⚠️ A job you bid on was cancelled or reopened.");
        } else if (
          ["completed", "customer_confirmed", "auto_confirmed"].includes(
            update.status
          )
        ) {
          toast.success("✅ One of your jobs has been completed.");
        }
        safeFetch();
      });

      socket.on("bid:changeUpdate", (data) => {
        if (String(data.workerId) === String(workerId)) {
          if (data.status === "approved")
            toast.success(`💰 Bid change for "${data.jobTitle}" accepted!`);
          else if (data.status === "rejected")
            toast.error(`❌ Bid change for "${data.jobTitle}" rejected.`);
          safeFetch();
        }
      });

      socket.on("bid:changeRequest", (info) => {
        if (String(info.workerId) === String(workerId)) {
          toast(`💬 ${info.customerName || "Customer"} viewed your bid change.`, {
            icon: "👀",
          });
          safeFetch();
        }
      });

      socket.on("worker:refresh", safeFetch);
      socket.on("connect", () => socket.emit("registerWorker", { workerId }));
    } catch (e) {
      console.warn("⚠️ Socket connection issue:", e.message);
    }

    return () => {
      socket.off("job:update");
      socket.off("bid:changeUpdate");
      socket.off("bid:changeRequest");
      socket.off("worker:refresh");
      socket.off("connect");
      clearTimeout(window._fetchTimer);
    };
  }, [workerId, navigate]);

  // ------- Change bid handlers
  const handleSubmitBidChange = async (bidId) => {
    if (!newBidAmount) {
      toast.error("Enter a new bid amount");
      return;
    }
    try {
      await axios.post(`${API_WRK}/job/bids/change`, {
        bidId,
        newAmount: newBidAmount,
        message: newBidMessage,
      });
      toast.success("Bid change request sent ✅");
      fetchBids();
      setActiveBid(null);
      setNewBidAmount("");
      setNewBidMessage("");
    } catch (err) {
      console.error("❌ Error changing bid:", err);
      toast.error("Failed to change bid");
    }
  };

  const handleCancelBidChange = async (bidId) => {
    try {
      await axios.post(`${API_WRK}/job/bids/change/cancel`, {
        bidId,
        workerId,
      });
      toast.success("Bid change request cancelled ❌");
      fetchBids();
    } catch (err) {
      console.error("❌ Cancel bid change failed:", err);
      toast.error("Failed to cancel bid change");
    }
  };

  // ------- Grouping
  const sortedBids = [...bids].sort(
    (a, b) =>
      new Date(b?.createdAt || 0).getTime() -
      new Date(a?.createdAt || 0).getTime()
  );

  const submitted = [];
  const accepted = [];
  const rejected = [];

  sortedBids.forEach((bid) => {
  const job = bid.jobId;
  if (!job) return;
  const jobStatus = (job.status || "").trim();
  const assignedTo = String(job.assignedTo?._id || job.assignedTo || "");

  // ❌ Hide only fully finished jobs
  if (HIDE_STATUSES.has(jobStatus)) return;

  // 🔵 REOPENED → Worker should see in Submitted
  if (jobStatus === "reopened") {
    submitted.push({ bid, job });
    return;
  }

  // 🟢 If worker accepted
  if (assignedTo === String(workerId)) {
    accepted.push({ bid, job });
    return;
  }

  // 🔴 If someone else is assigned
  if (assignedTo) {
    rejected.push({ bid, job });
    return;
  }

  // 🔵 Default: submitted
  submitted.push({ bid, job });
});

  const current =
    selectedTab === "submitted"
      ? submitted
      : selectedTab === "accepted"
      ? accepted
      : rejected;

  return (
    <div className="min-h-screen bg-gray-50 px-4 md:px-6 py-4">
      {/* Tabs only */}
      <div className="flex justify-center mb-6">
        <div className="bg-white border border-gray-200 rounded-full p-1 shadow-sm flex">
          {["submitted", "accepted", "rejected"].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={`px-6 py-2 text-sm md:text-base rounded-full font-medium transition-all ${
                selectedTab === tab
                  ? tab === "submitted"
                    ? "bg-blue-600 text-white"
                    : tab === "accepted"
                    ? "bg-green-600 text-white"
                    : "bg-red-600 text-white"
                  : "text-gray-700 hover:text-gray-900"
              }`}
            >
              {tab === "submitted"
                ? "Submitted"
                : tab === "accepted"
                ? "Accepted"
                : "Rejected"}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <AnimatePresence mode="popLayout">
          {current.length ? (
            current.map(({ bid, job }) => (
              <motion.div
                key={bid._id}
                layout
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                      {job.jobTitle || "Untitled Job"}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Your Bid: <strong>${bid.price}</strong>
                    </p>
                    <p className="text-sm text-gray-600">
                      Scheduled:{" "}
                      {job.scheduledAt
                        ? new Date(job.scheduledAt).toLocaleString()
                        : "ASAP"}
                    </p>
                    {job.description && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {job.description}
                      </p>
                    )}
                    {selectedTab !== "accepted" && (
                      <p className="text-xs text-gray-400 italic mt-1">
                        (Address hidden until job is awarded)
                      </p>
                    )}
                  </div>

                  {/* Status pill */}
                  <span
                    className={`shrink-0 inline-block px-3 py-1 text-xs rounded-full font-semibold ${
                      selectedTab === "submitted"
                        ? "bg-yellow-100 text-yellow-700"
                        : selectedTab === "accepted"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {selectedTab === "submitted"
                      ? "Waiting"
                      : selectedTab === "accepted"
                      ? "Accepted"
                      : "Rejected"}
                  </span>
                </div>

                {/* Change request box */}
                {bid.changeRequest && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3"
                  >
                    <p className="text-yellow-800 text-sm font-semibold">
                      Requested Change: ${bid.changeRequest.newPrice}
                    </p>
                    {bid.changeRequest.message && (
                      <p className="text-yellow-700 text-xs italic mt-1">
                        “{bid.changeRequest.message}”
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-yellow-700">
                        Status: {bid.changeRequest.status || "pending"}
                      </p>
                      {bid.changeRequest.status === "pending" && (
                        <button
                          onClick={() => handleCancelBidChange(bid._id)}
                          className="text-xs bg-red-600 text-white px-3 py-1 rounded-md hover:bg-red-700"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Buttons */}
                <div className="mt-4 flex gap-2">
                  {selectedTab === "submitted" ? (
                    <>
                      <button
                        onClick={() => setActiveBid(bid)}
                        disabled={bid.changeRequest?.status === "pending"}
                        className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition ${
                          bid.changeRequest?.status === "pending"
                            ? "bg-gray-400 text-white cursor-not-allowed"
                            : "bg-yellow-500 hover:bg-yellow-600 text-white"
                        }`}
                      >
                        {bid.changeRequest?.status === "pending"
                          ? "Awaiting Response"
                          : "Change Bid"}
                      </button>
                      <button
                        onClick={() => {
                          const custId =
                            job.customerId?._id || job.customerId;
                          navigate(
                            `/chat/${workerId}/${custId}/${job._id}`
                          );
                        }}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center justify-center gap-1"
                      >
                        <MessageSquare size={16} /> Chat
                      </button>
                    </>
                  ) : selectedTab === "accepted" ? (
                    <button
                      onClick={() => navigate("/my-jobs")}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
                    >
                      Go to My Jobs
                    </button>
                  ) : (
                    <button
                      className="flex-1 bg-gray-100 text-gray-600 px-4 py-2 rounded-md text-sm font-semibold cursor-default"
                    >
                      Not Selected
                    </button>
                  )}
                </div>
              </motion.div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-16 col-span-full">
              <AlertCircle className="mx-auto mb-2 w-6 h-6 opacity-60" />
              No jobs found.
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Floating Change Bid Modal */}
      <AnimatePresence>
        {activeBid && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveBid(null)}
            />
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 24 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 24 }}
              transition={{ duration: 0.18 }}
              className="fixed inset-x-4 top-[50%] z-50 -translate-y-1/2 bg-white rounded-2xl p-6 w-auto max-w-md shadow-2xl border border-gray-200 safe-area-adjust"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <RefreshCw className="text-blue-600" size={18} />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Change Your Bid
                  </h3>
                </div>
                <button
                  onClick={() => setActiveBid(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              <input
                type="number"
                placeholder="New amount"
                value={newBidAmount}
                onChange={(e) => setNewBidAmount(e.target.value)}
                className="w-full border rounded-lg px-4 py-2 mb-3 focus:ring-2 focus:ring-blue-500 outline-none"
              />
              <textarea
                placeholder="Message to customer (optional)"
                value={newBidMessage}
                onChange={(e) => setNewBidMessage(e.target.value)}
                className="w-full border rounded-lg px-4 py-2 mb-4 focus:ring-2 focus:ring-blue-500 outline-none"
                rows={3}
              />

              <div className="flex gap-3">
                <button
                  onClick={() => handleSubmitBidChange(activeBid._id)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md font-semibold"
                >
                  Submit
                </button>
                <button
                  onClick={() => setActiveBid(null)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-md font-semibold"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AppliedJobs;
